import type { Building } from "building-types/building/buildingTypes";
import { ensureDefined } from "common/utils/assertOfType";
import type { GenerateBuildingOptions } from "../types/generateBuildingTypes";

/**
 * Shared queue management for building generation. The helpers in this module coordinate
 * cancellation so that only the latest invocation per queue identifier resolves.
 */

export const NEW_CALL_ABORT_REASON = "New generateBuilding call started";

/**
 * Error thrown when a building generation call is cancelled by a newer call.
 */
export class CancelledByNewerCallError extends Error {
  constructor() {
    super("Cancelled by newer call");
    this.name = "CancelledByNewerCallError";
  }
}

type QueueState = {
  currentAbortController: AbortController | null;
  latestQueuedOptions: GenerateBuildingOptions | null;
  sharedQueuePromise: Promise<Building> | null;
  latestGenerationToken: number;
};

const queueStates = new Map<string, QueueState>();

/**
 * Returns the queue state associated with the provided identifier, creating one if needed.
 * The identifier must be a stable string.
 */
export function getOrCreateQueueState(queueId: string): QueueState {
  const existingState = queueStates.get(queueId);
  if (existingState) {
    return existingState;
  }

  const newState: QueueState = {
    currentAbortController: null,
    latestQueuedOptions: null,
    sharedQueuePromise: null,
    latestGenerationToken: 0,
  };

  queueStates.set(queueId, newState);
  return newState;
}

/**
 * Returns true if the provided error corresponds to a cancellation triggered by a newer call.
 */
export function isCancelledByNewerCall(error: unknown): boolean {
  return error instanceof CancelledByNewerCallError;
}

export function queueGenerateBuilding(
  options: GenerateBuildingOptions,
  generator: (options: GenerateBuildingOptions, signal: AbortSignal) => Promise<Building>,
): Promise<Building> {
  const queueId = options.queueId;
  if (!queueId) {
    // No queue required; execute immediately with an isolated AbortController.
    const abortController = new AbortController();
    return generator(options, abortController.signal);
  }

  const queueState = getOrCreateQueueState(queueId);

  queueState.latestQueuedOptions = options;
  queueState.latestGenerationToken += 1;

  if (queueState.currentAbortController) {
    // Abort the previous run so the generator can surface the cancellation quickly.
    queueState.currentAbortController.abort(NEW_CALL_ABORT_REASON);
  }

  if (!queueState.sharedQueuePromise) {
    // Start processing the queue. Later calls reuse this promise until the loop finishes.
    queueState.sharedQueuePromise = (async (): Promise<Building> => {
      try {
        return await runLatestGeneration(queueState, generator);
      } finally {
        // Clear the shared promise so a future call can start a new run.
        queueState.sharedQueuePromise = null;
        const latestState = queueStates.get(queueId);
        if (
          latestState === queueState &&
          queueState.currentAbortController === null &&
          queueState.latestQueuedOptions === null
        ) {
          queueStates.delete(queueId);
        }
      }
    })();
  }

  return ensureDefined(queueState.sharedQueuePromise);
}

async function runLatestGeneration(
  queueState: QueueState,
  generator: (options: GenerateBuildingOptions, signal: AbortSignal) => Promise<Building>,
): Promise<Building> {
  while (true) {
    // Take snapshots so we can detect if a newer call supersedes this run.
    const optionsSnapshot = ensureDefined(queueState.latestQueuedOptions);
    const tokenSnapshot = queueState.latestGenerationToken;
    const abortController = new AbortController();
    queueState.currentAbortController = abortController;

    try {
      const building = await generator(optionsSnapshot, abortController.signal);
      if (tokenSnapshot !== queueState.latestGenerationToken) {
        // A newer request arrived while awaiting; loop again with the fresh options.
        continue;
      }
      queueState.latestQueuedOptions = null;
      return building;
    } catch (error: unknown) {
      if (isCancelledByNewerCall(error)) {
        // The generator finished early due to cancellation; continue to run the latest call.
        continue;
      }
      queueState.latestQueuedOptions = null;
      throw error;
    } finally {
      if (queueState.currentAbortController === abortController) {
        queueState.currentAbortController = null;
      }
    }
  }
}
